import { useState, useEffect, FormEvent } from 'react';
import apiClient from '../../services/api';
import { Role } from '../../types/auth';
import { showToast } from '../ui/Toast';
import { ConfirmDialog } from '../ui/ConfirmDialog';

const RoleManagement = () => {
    const [roles, setRoles] = useState<Role[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingRole, setEditingRole] = useState<Role | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({ name: '', description: '' });
    const [error, setError] = useState('');
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

    useEffect(() => {
        fetchRoles();
    }, []);

    const fetchRoles = async () => {
        try {
            const response = await apiClient.get('/roles');
            setRoles(response.data.data);
        } catch (error) {
            console.error('Failed to fetch roles:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError('');

        try {
            if (editingRole) {
                await apiClient.put(`/roles/${editingRole.id}`, formData);
            } else {
                await apiClient.post('/roles', formData);
            }

            setIsModalOpen(false);
            setEditingRole(null);
            setFormData({ name: '', description: '' });
            fetchRoles();
        } catch (err: any) {
            setError(err.response?.data?.error?.message || 'Failed to save role');
        }
    };

    const handleDelete = async (id: string) => {
        try {
            await apiClient.delete(`/roles/${id}`);
            setPendingDeleteId(null);
            showToast('Role deleted successfully', 'success');
            fetchRoles();
        } catch (error) {
            console.error('Failed to delete role:', error);
            showToast('Failed to delete role', 'error');
        }
    };

    const openModal = (role?: Role) => {
        if (role) {
            setEditingRole(role);
            setFormData({ name: role.name, description: role.description || '' });
        } else {
            setEditingRole(null);
            setFormData({ name: '', description: '' });
        }
        setIsModalOpen(true);
    };

    if (loading) return <div className="p-8 text-center">Loading...</div>;

    return (
        <>
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">Role Management</h1>
                    <p className="text-slate-500 mt-1">Manage user roles and permissions</p>
                </div>
                <button
                    onClick={() => openModal()}
                    className="btn-primary flex items-center gap-2"
                >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Role
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {roles.map((role) => (
                    <div key={role.id} className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-4">
                            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${role.isSystem ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}`}>
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => openModal(role)}
                                    className="text-slate-400 hover:text-blue-600 transition-colors"
                                >
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                </button>
                                {!role.isSystem && (
                                    <button
                                        onClick={() => setPendingDeleteId(role.id)}
                                        className="text-slate-400 hover:text-red-600 transition-colors"
                                    >
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-semibold text-slate-800">{role.name}</h3>
                            {role.isSystem && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
                                    System
                                </span>
                            )}
                        </div>
                        <p className="text-slate-500 text-sm">{role.description || 'No description provided'}</p>
                    </div>
                ))}
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl animate-fade-in">
                        <h2 className="text-2xl font-bold text-slate-800 mb-6">
                            {editingRole ? 'Edit Role' : 'Create Role'}
                        </h2>

                        {error && (
                            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm border border-red-100">
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Role Name</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                    className="input-field"
                                    placeholder="e.g. MANAGER"
                                    disabled={editingRole?.isSystem === true}
                                />
                                {editingRole?.isSystem && <p className="text-xs text-slate-500 mt-1">System role names cannot be changed.</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="input-field min-h-[100px]"
                                    placeholder="Optional description..."
                                />
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="flex-1 btn-secondary"
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="flex-1 btn-primary">
                                    {editingRole ? 'Save Changes' : 'Create Role'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>

            <ConfirmDialog
                open={!!pendingDeleteId}
                title="Delete Role"
                message="Are you sure you want to delete this role?"
                confirmLabel="Delete"
                variant="danger"
                onConfirm={() => pendingDeleteId && handleDelete(pendingDeleteId)}
                onCancel={() => setPendingDeleteId(null)}
            />
        </>
    );
};

export default RoleManagement;
